<template>
    <div class="p-6 max-w-4xl mx-auto">
        <!-- Header -->
        <div class="mb-8">
            <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Search Management
            </h1>
            <p class="text-gray-600 dark:text-gray-400">
                Manage and recreate search indexes for your content. Select the indexes you want to rebuild and click "Recreate Indexes".
            </p>
        </div>

        <!-- Artist Import -->
        <ArtistImportForm class="mb-8" />

        <!-- Main Card -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
            <!-- Select All Toggle -->
            <div class="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                <label class="flex items-center cursor-pointer group">
                    <input 
                        type="checkbox" 
                        @change="toggleAll" 
                        :checked="allSelected"
                        class="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-indigo-600"
                    />
                    <span class="ml-3 text-lg font-semibold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                        Select All Indexes
                    </span>
                </label>
            </div>

            <!-- Checkbox Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <!-- Songs -->
                <label class="flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all hover:border-indigo-300 dark:hover:border-indigo-700"
                       :class="indexesToRecreate.lyrics ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-700'">
                    <input 
                        @change="toggleValue" 
                        type="checkbox" 
                        value="lyrics" 
                        :checked="indexesToRecreate.lyrics"
                        class="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <div class="ml-3 flex-1">
                        <div class="text-base font-medium text-gray-900 dark:text-white">Songs</div>
                        <div class="text-sm text-gray-500 dark:text-gray-400">Lyrics and song content</div>
                    </div>
                    <svg v-if="indexesToRecreate.lyrics" class="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                    </svg>
                </label>

                <!-- Playlists -->
                <label class="flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all hover:border-indigo-300 dark:hover:border-indigo-700"
                       :class="indexesToRecreate.playlists ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-700'">
                    <input 
                        @change="toggleValue" 
                        type="checkbox" 
                        value="playlists" 
                        :checked="indexesToRecreate.playlists"
                        class="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <div class="ml-3 flex-1">
                        <div class="text-base font-medium text-gray-900 dark:text-white">Playlists</div>
                        <div class="text-sm text-gray-500 dark:text-gray-400">Playlist names and metadata</div>
                    </div>
                    <svg v-if="indexesToRecreate.playlists" class="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                    </svg>
                </label>

                <!-- Artists -->
                <label class="flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all hover:border-indigo-300 dark:hover:border-indigo-700"
                       :class="indexesToRecreate.artists ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-700'">
                    <input 
                        @change="toggleValue" 
                        type="checkbox" 
                        value="artists" 
                        :checked="indexesToRecreate.artists"
                        class="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <div class="ml-3 flex-1">
                        <div class="text-base font-medium text-gray-900 dark:text-white">Artists</div>
                        <div class="text-sm text-gray-500 dark:text-gray-400">Artist names and information</div>
                    </div>
                    <svg v-if="indexesToRecreate.artists" class="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                    </svg>
                </label>

                <!-- Composers -->
                <label class="flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all hover:border-indigo-300 dark:hover:border-indigo-700"
                       :class="indexesToRecreate.composers ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-700'">
                    <input 
                        @change="toggleValue" 
                        type="checkbox" 
                        value="composers" 
                        :checked="indexesToRecreate.composers"
                        class="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <div class="ml-3 flex-1">
                        <div class="text-base font-medium text-gray-900 dark:text-white">Composers</div>
                        <div class="text-sm text-gray-500 dark:text-gray-400">Composer names and data</div>
                    </div>
                    <svg v-if="indexesToRecreate.composers" class="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                    </svg>
                </label>

                <!-- Tags -->
                <label class="flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all hover:border-indigo-300 dark:hover:border-indigo-700"
                       :class="indexesToRecreate.tags ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-700'">
                    <input 
                        @change="toggleValue" 
                        type="checkbox" 
                        value="tags" 
                        :checked="indexesToRecreate.tags"
                        class="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <div class="ml-3 flex-1">
                        <div class="text-base font-medium text-gray-900 dark:text-white">Tags</div>
                        <div class="text-sm text-gray-500 dark:text-gray-400">Content tags and labels</div>
                    </div>
                    <svg v-if="indexesToRecreate.tags" class="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                    </svg>
                </label>
            </div>

            <!-- Selected Count -->
            <div v-if="selectedCount > 0" class="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg border border-indigo-200 dark:border-indigo-700">
                <div class="flex items-center gap-2">
                    <svg class="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                    </svg>
                    <span class="text-sm font-medium text-indigo-900 dark:text-indigo-200">
                        {{ selectedCount }} index{{ selectedCount > 1 ? 'es' : '' }} selected
                    </span>
                </div>
            </div>

            <!-- Action Button -->
            <div class="flex items-center gap-4">
                <button
                    @click="recreate"
                    :disabled="loading || selectedCount === 0"
                    class="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                    <svg v-if="!loading" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>
                    <svg v-else class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{{ loading ? 'Recreating...' : 'Recreate Indexes' }}</span>
                </button>
                <button
                    @click="resetSelection"
                    :disabled="loading"
                    class="px-4 py-3 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Reset
                </button>
            </div>

            <!-- Status Message -->
            <div v-if="statusMessage" class="mt-6 p-4 rounded-lg"
                 :class="statusMessage.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700'">
                <div class="flex items-start gap-3">
                    <svg v-if="statusMessage.type === 'success'" class="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                    </svg>
                    <svg v-else class="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                    </svg>
                    <div class="flex-1">
                        <p class="text-sm font-medium"
                           :class="statusMessage.type === 'success' ? 'text-green-900 dark:text-green-200' : 'text-red-900 dark:text-red-200'">
                            {{ statusMessage.text }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref, computed } from "vue";
import toastStore from '@/store/toast.store';
import ArtistImportForm from '@/components/admin/ArtistImportForm.vue';

const indexesToRecreate = ref({
    'lyrics': false,
    'playlists': false,
    'artists': false,
    'composers': false,
    'tags': false
});

const loading = ref(false);
const statusMessage = ref(null);

// Computed properties
const selectedCount = computed(() => {
    return Object.values(indexesToRecreate.value).filter(v => v === true).length;
});

const allSelected = computed(() => {
    const allIndexes = Object.keys(indexesToRecreate.value);
    return selectedCount.value === allIndexes.length && allIndexes.length > 0;
});

// Methods
const toggleValue = (event) => {
    indexesToRecreate.value[event.target.value] = event.target.checked;
    clearStatusMessage();
};

const toggleAll = (event) => {
    const checked = event.target.checked;
    Object.keys(indexesToRecreate.value).forEach(key => {
        indexesToRecreate.value[key] = checked;
    });
    clearStatusMessage();
};

const resetSelection = () => {
    Object.keys(indexesToRecreate.value).forEach(key => {
        indexesToRecreate.value[key] = false;
    });
    clearStatusMessage();
};

const clearStatusMessage = () => {
    statusMessage.value = null;
};

const recreate = async () => {
    const toRecreate = Object.entries(indexesToRecreate.value).filter( i => i[1] == true);
    
    if (toRecreate.length === 0) {
        statusMessage.value = {
            type: 'error',
            text: 'Please select at least one index to recreate.'
        };
        return;
    }
    
    const query = toRecreate.map( i => i[0]).join(",");
    loading.value = true;
    clearStatusMessage();
    
    try {
        const resp = await fetch("/api/admin/algolia/recreate?indexes="+query);
        const response = await resp.json();
        
        let successMessage = '';
        Object.entries(response.indexes || response).forEach((r) => {
            if (typeof r[1] === 'object' && r[1].created !== undefined) {
                successMessage += `${r[0]}: ${r[1].created} items created. `;
            }
        });
        
        if (successMessage) {
            statusMessage.value = {
                type: 'success',
                text: successMessage.trim()
            };
            toastStore.dispatch('addMessage', {
                message: successMessage.trim(),
                icon: 'success'
            });
        } else {
            statusMessage.value = {
                type: 'success',
                text: 'Indexes recreated successfully.'
            };
        }
        
        // Auto-clear status message after 10 seconds
        setTimeout(() => {
            clearStatusMessage();
        }, 10000);
        
    } catch (error) {
        statusMessage.value = {
            type: 'error',
            text: 'Failed to recreate indexes. Please try again.'
        };
        toastStore.dispatch('addMessage', {
            message: 'Failed to recreate indexes',
            icon: 'error'
        });
    } finally {
        loading.value = false;
    }
};

</script>